import ProductBrandLogo from '@/components/Brand/ProductBrandLogo';
import { useTranslation } from '@client-app/i18n';
import { ArrowSquareOut, CircleNotch } from '@phosphor-icons/react';
import type { PluginOAuthSuccessState } from './oauthSuccessPresentation';

type PluginOAuthSuccessViewProps = {
    state: PluginOAuthSuccessState;
    onAction: () => void | Promise<void>;
};

const PluginOAuthSuccessView = ({
    state,
    onAction,
}: PluginOAuthSuccessViewProps) => {
    const { t } = useTranslation();
    const isOpening = state === 'opening';
    const isError = state === 'error';
    const messageKey = isError
        ? 'website.clientAuth.pluginHandoffError'
        : isOpening
            ? 'website.clientAuth.pluginOpeningMessage'
            : 'website.clientAuth.pluginSuccessMessage';
    const actionKey = isError
        ? 'website.clientAuth.pluginRetryAction'
        : isOpening
            ? 'website.clientAuth.pluginOpeningAction'
            : 'website.clientAuth.openAiApp';

    return (
        <div
            data-product-variant="plugin"
            data-oauth-state={state}
            className="fixed inset-0 z-[90] flex min-h-screen flex-col bg-[#F4F5F8] antialiased"
        >
            <header className="absolute left-8 top-8 z-[92] sm:left-10 sm:top-10">
                <ProductBrandLogo
                    size="24px"
                    className="block max-w-[190px] object-contain"
                />
            </header>

            <main className="flex flex-1 items-center justify-center px-6">
                <div className="flex -translate-y-2 flex-col items-center text-center animate-in fade-in duration-300">
                    <p
                        className="mb-8 text-[18px] font-normal leading-7 text-[#666F8D]"
                        role={isError ? 'alert' : 'status'}
                    >
                        {t(messageKey)}
                    </p>

                    <button
                        type="button"
                        onClick={() => {
                            void onAction();
                        }}
                        disabled={isOpening}
                        aria-busy={isOpening ? 'true' : undefined}
                        aria-label={t(actionKey)}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-[#E8EBF3] bg-white px-4 text-[14px] font-medium text-[#1A162F] shadow-sm transition-all duration-200 hover:border-[#C8D0E2] hover:shadow-md focus-visible:border-[#1A162F] focus-visible:ring-2 focus-visible:ring-[#1A162F]/20 disabled:cursor-wait disabled:bg-[#D1D5DB] disabled:text-white disabled:shadow-none"
                    >
                        {isOpening ? (
                            <CircleNotch size={16} weight="bold" className="animate-spin" />
                        ) : (
                            <ArrowSquareOut size={16} weight="bold" />
                        )}
                        <span>{t(actionKey)}</span>
                    </button>
                </div>
            </main>
        </div>
    );
};

export default PluginOAuthSuccessView;
